import { BrowserRouter, Navigate, Route, Routes, useSearchParams } from 'react-router-dom';
import {
  appendNext,
  AuthGuard,
  ForgotPassword,
  GoogleRedirectCallback,
  Login,
  peekReturnTo,
  ResetPassword,
  resolveReturnTo,
  sanitizeReturnTo,
  useAuth,
} from '@dental-staffing-platform/auth';
import { AdminLayout } from './screens/AdminLayout.js';
import { Overview } from './screens/Overview.js';
import { CredentialReview } from './screens/CredentialReview.js';
import { Professionals } from './screens/Professionals.js';
import { Invites } from './screens/Invites.js';
import { Operations } from './screens/Operations.js';
import { Audit } from './screens/Audit.js';
import { SystemHealth } from './screens/SystemHealth.js';
import { Payments } from './screens/Payments.js';
import { MessageDeliveries } from './screens/MessageDeliveries.js';
import { PayoutOnboarding } from './screens/PayoutOnboarding.js';
import { TaxDocuments } from './screens/TaxDocuments.js';
import { SupportCases } from './screens/SupportCases.js';
import { SupportCaseDetail } from './screens/SupportCaseDetail.js';
import {
  ReviewModeration,
  ModeratedReviewDetail,
  ModerationCaseDetail,
} from './screens/ReviewModeration.js';

const DEMO =
  import.meta.env.DEV || import.meta.env.VITE_SHOW_DEMO_ACCOUNTS === 'true'
    ? [{ label: 'Admin demo', email: 'office@brightsmiles.dev', password: 'Dental#2026' }]
    : undefined;

/** Login screen that redirects into the console once authenticated (fixes "stuck on login"). */
function LoginRoute() {
  const { status } = useAuth();
  const [params] = useSearchParams();
  const nextQuery = sanitizeReturnTo(params.get('next'));
  if (status === 'authenticated') {
    return <Navigate to={resolveReturnTo(nextQuery, '/')} replace />;
  }
  return (
    <Login
      title="Dental Staffing — Admin"
      subtitle="Sign in to the admin console"
      {...(DEMO ? { demoAccounts: DEMO } : {})}
      googleSignInEnabled={import.meta.env.VITE_GOOGLE_AUTH_ENABLED === 'true'}
      googleReturnTo={nextQuery ?? peekReturnTo() ?? '/'}
      forgotPasswordHref={appendNext('/forgot-password', nextQuery)}
    />
  );
}

function ForgotPasswordRoute() {
  const [params] = useSearchParams();
  const next = sanitizeReturnTo(params.get('next'));
  return <ForgotPassword loginHref={appendNext('/login', next)} />;
}

function ResetPasswordRoute() {
  const [params] = useSearchParams();
  const next = sanitizeReturnTo(params.get('next'));
  return (
    <ResetPassword
      token={params.get('token')}
      loginHref={appendNext('/login', next)}
      forgotPasswordHref={appendNext('/forgot-password', next)}
    />
  );
}

/** Admin app routes: login is open; the console requires the `admin` role. */
export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/auth/google/callback" element={<GoogleRedirectCallback />} />
        <Route path="/forgot-password" element={<ForgotPasswordRoute />} />
        <Route path="/reset-password" element={<ResetPasswordRoute />} />
        <Route path="/set-password" element={<ResetPasswordRoute />} />
        <Route
          element={
            <AuthGuard requireRole="admin">
              <AdminLayout />
            </AuthGuard>
          }
        >
          <Route path="/" element={<Overview />} />
          <Route path="/invites" element={<Invites />} />
          <Route path="/credentials" element={<CredentialReview />} />
          <Route path="/professionals" element={<Professionals />} />
          <Route path="/operations" element={<Operations />} />
          <Route path="/payments" element={<Payments />} />
          <Route path="/payout-onboarding" element={<PayoutOnboarding />} />
          <Route path="/tax-documents" element={<TaxDocuments />} />
          <Route path="/review-moderation" element={<ReviewModeration />} />
          <Route path="/review-moderation/reviews/:reviewId" element={<ModeratedReviewDetail />} />
          <Route path="/review-moderation/cases/:caseId" element={<ModerationCaseDetail />} />
          <Route path="/support" element={<SupportCases />} />
          <Route path="/support/:caseId" element={<SupportCaseDetail />} />
          <Route path="/deliveries" element={<MessageDeliveries />} />
          <Route path="/audit" element={<Audit />} />
          <Route path="/system" element={<SystemHealth />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
